import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { WEIGHT_ITEM_CODE } from "@/shared/models/SectionSpec";
import { downloadUploadFile } from "@/shared/repositories/SectionSpecRepository";
import { getTableRows } from "@/shared/utils/storedProcedureRows";
import type {
	TestShackReport,
	TestShackReportItem,
	TestShackReportEventOption,
	TestShackReportFilterOptions,
	TestShackReportFiltersRequest,
	TestShackReportHeatOption,
	TestShackReportInspection,
	TestShackReportInspectionSummary,
	TestShackReportLegDelta,
	TestShackReportRequest,
	TestShackReportSectionOption,
} from "../models/TestShackReport";

const moment = getMomentTz();

interface FileSignature {
	mediaType: string;
	bytes: readonly number[];
	offset?: number;
}

const FILE_SIGNATURES: readonly FileSignature[] = [
	{ mediaType: "image/png", bytes: [0x89, 0x50, 0x4e, 0x47] },
	{ mediaType: "image/jpeg", bytes: [0xff, 0xd8, 0xff] },
	{ mediaType: "image/gif", bytes: [0x47, 0x49, 0x46, 0x38] },
	{ mediaType: "image/bmp", bytes: [0x42, 0x4d] },
	{ mediaType: "image/webp", bytes: [0x57, 0x45, 0x42, 0x50], offset: 8 },
	{ mediaType: "application/pdf", bytes: [0x25, 0x50, 0x44, 0x46] },
];

const matchesSignature = (
	fileBytes: Uint8Array,
	{ bytes, offset = 0 }: FileSignature,
): boolean => bytes.every((byte, index) => fileBytes[offset + index] === byte);

const withPreviewMediaType = async (blob: Blob): Promise<Blob> => {
	if (blob.type && blob.type !== "application/octet-stream") return blob;

	const fileBytes = new Uint8Array(await blob.slice(0, 256).arrayBuffer());
	const signature = FILE_SIGNATURES.find((candidate) =>
		matchesSignature(fileBytes, candidate),
	);
	const headerText = new TextDecoder().decode(fileBytes).trimStart();
	const mediaType =
		signature?.mediaType ??
		(headerText.startsWith("<svg") || headerText.includes("<svg")
			? "image/svg+xml"
			: null);

	return mediaType ? blob.slice(0, blob.size, mediaType) : blob;
};

type WeightFields =
	| "MeasuredLbsPerFoot"
	| "TargetLbsPerFoot"
	| "MinLbsPerFoot"
	| "MaxLbsPerFoot"
	| "WeightStatus";

interface TestShackReportInspectionTransport
	extends Omit<
		TestShackReportInspectionSummary,
		| WeightFields
		| "ProductionDate"
		| "ShiftStart"
		| "ShiftEnd"
		| "EventStart"
		| "EventEnd"
		| "InspectedAt"
	> {
	ProductionDate: string;
	ShiftStart: string;
	ShiftEnd: string;
	EventStart: string | null;
	EventEnd: string | null;
	InspectedAt: string;
}

interface TestShackReportEventTransport
	extends Omit<TestShackReportEventOption, "Start" | "End"> {
	Start: string | null;
	End: string | null;
}

const toUtcDate = (value: string): Date => moment.utc(value).toDate();

const toNullableUtcDate = (value: string | null): Date | null =>
	value === null ? null : toUtcDate(value);

const toWeight = (
	item: TestShackReportItem | undefined,
): Pick<TestShackReportInspectionSummary, WeightFields> => ({
	MeasuredLbsPerFoot: item?.NumericValue ?? null,
	TargetLbsPerFoot: item?.Nominal ?? null,
	MinLbsPerFoot: item?.Ltl ?? null,
	MaxLbsPerFoot: item?.Utl ?? null,
	WeightStatus:
		item === undefined || item.NumericValue === null ? null : item.Status,
});

const toInspectionSummary = (
	row: TestShackReportInspectionTransport,
	weight: TestShackReportItem | undefined,
): TestShackReportInspectionSummary => ({
	...row,
	...toWeight(weight),
	ProductionDate: toUtcDate(row.ProductionDate),
	ShiftStart: toUtcDate(row.ShiftStart),
	ShiftEnd: toUtcDate(row.ShiftEnd),
	EventStart: toNullableUtcDate(row.EventStart),
	EventEnd: toNullableUtcDate(row.EventEnd),
	InspectedAt: toUtcDate(row.InspectedAt),
});

export const groupRowsByInspection = <
	TRow extends { ProfileInspectionId: number },
>(
	rows: TRow[],
): Map<number, TRow[]> => {
	const rowsByInspection = new Map<number, TRow[]>();

	for (const row of rows) {
		const inspectionRows = rowsByInspection.get(row.ProfileInspectionId) ?? [];
		rowsByInspection.set(row.ProfileInspectionId, [...inspectionRows, row]);
	}

	return rowsByInspection;
};

export const getTestShackReport = async (
	request: TestShackReportRequest,
	signal?: AbortSignal,
): Promise<TestShackReport> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: request.assetId },
		{ name: "StartDate", value: request.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: request.endDate?.toISOString() ?? null },
		{ name: "ShiftId", value: request.shiftId },
		{ name: "SectionId", value: request.sectionId },
		{ name: "EventId", value: request.eventId },
		{ name: "HeatId", value: request.heatId },
		{ name: "ProblemsOnly", value: request.problemsOnly },
	];

	const response = await apiService.callV2(
		"[DIM].[GetProfileInspectionReport]",
		parameters,
		signal,
	);
	if (!response.ok) {
		throw new Error(response.message || "Unable to load the Test Shack report");
	}

	const itemsByInspection = groupRowsByInspection(
		getTableRows<TestShackReportItem>(response, 1),
	);
	const summaries = getTableRows<TestShackReportInspectionTransport>(
		response,
		0,
	).map((row) =>
		toInspectionSummary(
			row,
			itemsByInspection
				.get(row.ProfileInspectionId)
				?.find((item) => item.Code === WEIGHT_ITEM_CODE),
		),
	);
	const legDeltasByInspection = groupRowsByInspection(
		getTableRows<TestShackReportLegDelta>(response, 2),
	);
	const inspections: TestShackReportInspection[] = summaries.map((summary) => ({
		...summary,
		items: (itemsByInspection.get(summary.ProfileInspectionId) ?? []).filter(
			(item) => item.Code !== WEIGHT_ITEM_CODE,
		),
		legDeltas: legDeltasByInspection.get(summary.ProfileInspectionId) ?? [],
	}));

	return { inspections };
};

export const getTestShackReportFilters = async (
	request: TestShackReportFiltersRequest,
	signal?: AbortSignal,
): Promise<TestShackReportFilterOptions> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: request.assetId },
		{ name: "StartDate", value: request.startDate?.toISOString() ?? null },
		{ name: "EndDate", value: request.endDate?.toISOString() ?? null },
		{ name: "ShiftId", value: request.shiftId },
		{ name: "SectionId", value: request.sectionId },
		{ name: "EventId", value: request.eventId },
	];

	const response = await apiService.callV2(
		"[DIM].[GetProfileInspectionReportFilters]",
		parameters,
		signal,
	);
	if (!response.ok) {
		throw new Error(
			response.message || "Unable to load Test Shack report filters",
		);
	}

	const events = getTableRows<TestShackReportEventTransport>(
		response,
		1,
	).map<TestShackReportEventOption>((event) => ({
		...event,
		Start: toNullableUtcDate(event.Start),
		End: toNullableUtcDate(event.End),
	}));

	return {
		sections: getTableRows<TestShackReportSectionOption>(response, 0),
		events,
		heats: getTableRows<TestShackReportHeatOption>(response, 2),
	};
};

export const getTestShackReportImage = async (
	uploadFileId: number,
	signal?: AbortSignal,
): Promise<Blob> => {
	const blob = await downloadUploadFile(uploadFileId, signal);
	return await withPreviewMediaType(blob);
};
